import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import {
  AgencyLogo,
  Description,
  OffersCount,
  PhoneButtonContainer,
  PhonePlaceholder,
  Row,
  StyledAgencyCard,
  Title
} from './styled';
import { AgenciesQuery } from '../../../graphql/graphql';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import { AnalyticLogTargetTypeEnum } from '../../../graphql/types';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';

const BottomPhoneButton = dynamic(
  () => import('../../Ad/BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  { ssr: false }
);

export const AgencyCard: React.FC<AgenciesQuery['agencies']['0']> = React.memo(
  ({ id, path, logoUrl, name, itemsCount = 0, description }) => {
    const { t } = useTranslation();
    const { isSmall } = useScreenSize();
    const isClient = useClient();

    const offersCount = useMemo(
      () => t('sections.agencies.agencies_count_many', { count: itemsCount }),
      [t, itemsCount]
    );

    const agencyPathId = useMemo(
      () => path?.replace('/agentlikler/', '') ?? '',
      [path]
    );

    const { onClickHandler, phonesShown, setIsVisible, isVisible } =
      useFullPhoneNumbers({
        id: agencyPathId,
        residenceId: null,
        path: 'agentlikler',
        targetType: AnalyticLogTargetTypeEnum.Agency,
        agencyId: id
      });

    return (
      <StyledAgencyCard
        href={path}
        data-cy="agency"
        data-stat="agency-card-click"
      >
        <Row>
          <AgencyLogo
            src={logoUrl || '/static/images/empty-agency-logo.svg'}
            alt={`${name} Logo`}
            data-cy="agency-logo"
            width={55}
            height={55}
            loading="lazy"
          />
          <div>
            <Title data-cy="agency-title">{name}</Title>
            <OffersCount data-cy="agency-count" title={offersCount}>
              {offersCount}
            </OffersCount>
          </div>
        </Row>

        <Description tag="caption_1_new" data-cy="agency-desc">
          {description}
        </Description>

        {!isClient ? (
          <PhonePlaceholder
            width="55px"
            height="55px"
            borderRadius="50%"
            margin="0"
          />
        ) : (
          isSmall && (
            <PhoneButtonContainer>
              <BottomPhoneButton
                isGallery={false}
                isIconButton
                phones={phonesShown}
                clickHandler={onClickHandler}
                contactInfoModalTitle={name}
                {...{ setIsVisible, isVisible }}
                stat="agency-call-click"
              />
            </PhoneButtonContainer>
          )
        )}
      </StyledAgencyCard>
    );
  }
);

AgencyCard.displayName = 'AgencyCard';
